import { offerSigningActive } from './offer-signing-guard'
import { accountReloadSuppressed } from '@/utils/wallet/account-change-guard'
import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_PUBLIC_KEY,
	STORAGE_WALLET_TYPE,
	NETWORK,
	MOBILE_BREAKPOINT
} from '@/utils/constants'
import SafeLocalStorage from '../safe-local-storage'
import { allInputIndexes, extractFinalizedPsbtHex, normalizePsbtHex, parsePsbtTransaction } from './psbt'
import Wallet from './'
import { WalletProvider } from '@/utils/types/wallet'
import { isUserRejection } from './signing-errors'

/**
 * UniSat signs only the inputs owned by its active account. If the user
 * switched accounts since connecting, a purchase comes back with the
 * buyer's inputs unsigned and the API refuses it ("input 0 is missing
 * signature"). When UniSat already reports an account and it is not the
 * connected one, say so before signing. Locked, disconnected or older
 * extensions report nothing and keep their existing behaviour.
 */
async function assertSameUnisatAccount(): Promise<void> {
	let live = ''
	try {
		// @ts-ignore
		const accounts = await Promise.race([
			// @ts-ignore
			window.unisat?.getAccounts?.(),
			new Promise((resolve) => setTimeout(() => resolve(null), 1500))
		])
		live = Array.isArray(accounts) && typeof accounts[0] === 'string' ? accounts[0] : ''
	} catch {
		return
	}
	if (!live) return
	const known = [Wallet.getTaprootAddress(), Wallet.balanceAddress()]
		.filter((a): a is string => typeof a === 'string' && a.length > 0)
		.map((a) => a.toLowerCase())
	if (known.length === 0 || known.includes(live.toLowerCase())) return
	throw new Error(
		`UniSat is on a different account (${live}) than the one connected to Ordinals Wallet. Switch UniSat back to that account, or disconnect and reconnect, then try again.`
	)
}

export default class UnisatWallet implements WalletProvider {
	get name(): string {
		return 'Unisat'
	}

	get capabilities(): string[] {
		return []
	}

	get connectIcon(): string {
		return '/connect-unisat.png'
	}

	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.unisat) {
			const isMobile = window.innerWidth < MOBILE_BREAKPOINT

			if (isMobile) {
				throw new Error('Open this site in the UniSat in-app browser to connect on mobile.')
			}

			window.open('https://unisat.io/', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async listenForAccountChange(): Promise<void> {
		// @ts-ignore
		if (!window.unisat) {
			return
		}

		// @ts-ignore
		window.unisat.on('accountsChanged', async (accounts?: string[]) => {
			if (Wallet.type() !== 'unisat') {
				return
			}
			const keepOffer = offerSigningActive()
			const previousAddress = Wallet.getTaprootAddress()
			const previousKey = Wallet.getPublicKey()
			if (keepOffer && accounts?.[0] === previousAddress) return
			await this.login()
			if (accountReloadSuppressed()) return
			// An offer's unlock event may finish profile loading after signing.
			// Preserve only the same account; real account switches still reload.
			if (keepOffer && Wallet.getTaprootAddress() === previousAddress && Wallet.getPublicKey() === previousKey) return
			window.location.reload()
		})
	}

	async dummyPsbt(inscription_id: string): Promise<string> {
		const psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		return psbt
	}

	async login(): Promise<void> {
		// @ts-ignore
		if (!window.unisat) {
			throw new Error('UniSat is not installed. Install the extension, then reconnect.')
		}

		const { address, public_key } = await (window as any).unisat
			.requestAccounts()
			.then(async ([address]: string[]) => ({
				address,
				public_key: await (window as any).unisat.getPublicKey()
			}))
		localStorage.setItem(STORAGE_TAPROOT_ADDRESS, address)
		localStorage.setItem(STORAGE_WALLET_TYPE, 'unisat')
		localStorage.setItem(STORAGE_PUBLIC_KEY, public_key)
		try {
			await Wallet.fetchProfile()
		} catch (_e) {}
	}

	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	async signPSBTS(psbts: string[]): Promise<string[]> {
		const options = psbts.map((psbt) => {
			const psbt_tx = parsePsbtTransaction(psbt)

			return {
				autoFinalized: true,
				toSignInputs: allInputIndexes(psbt_tx).map((index) => ({
					index,
					address: Wallet.getTaprootAddress(),
					publicKey: Wallet.getPublicKey()
				}))
			}
		})

		try {
			// @ts-ignore
			const data = await window.unisat.signPsbts(psbts, options)
			return data
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBT(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await window.unisat.signPsbt(psbt)
			return normalizePsbtHex(data)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await window.unisat.signPsbt(psbt)
			return extractFinalizedPsbtHex(data)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signMessage(message: String): Promise<string> {
		try {
			// @ts-ignore
			const res = await window.unisat.signMessage(message, 'bip322-simple')
			return res
		} catch (_e) {
			return undefined as unknown as string
		}
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signTransfer(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signEscrow(psbt: string): Promise<string> {
		return this.signPSBT(psbt)
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		await assertSameUnisatAccount()
		try {
			let signedSetup = ''

			if (setup_psbt) {
				// @ts-ignore
				const signedSetupPSBT = await window.unisat.signPsbt(setup_psbt, {
					autoFinalized: true
				})
				signedSetup = extractFinalizedPsbtHex(signedSetupPSBT)
			}
			// @ts-ignore
			const signedPurchase = await window.unisat.signPsbt(purchase_psbt, {
				autoFinalized: true
			})
			return { signedSetup, signedPurchase }
		} catch (e) {
			if (isUserRejection(e)) {
				return undefined as unknown as { signedSetup: string; signedPurchase: string }
			}
			console.log(e)
			throw e
		}
	}
}
